package com.milta.core.localization

/**
 * MIL-98 — AI credit per account: the "Ajouter des livraisons" menu and the import screens (mobile),
 * the web import step, "Mon usage" (mobile Profile + web Paramètres) and the Admin "Limites
 * d'usage" / "Fiche utilisateur" pages. Placeholders between braces (`{percent}`, `{date}`...) are
 * replaced in code. The user only ever sees percentages; euros and tokens are Admin-only (R1).
 */
data class AiCreditStrings(
    // ── AI marks, exhausted banners (M1, E1) ──
    /** Mark of the modes that use the credit. */
    val aiMark: String,
    /** Retour 27/10 — note under the import sources: what "IA" means, and the spreadsheet case. */
    val menuAiLegend: String,
    /** M1 at 0 — "Crédit IA épuisé — remise à zéro le {date}." */
    val menuExhaustedTitle: String,
    val menuExhaustedMessage: String,
    /** Reason under a disabled AI mode. */
    val modeDisabledReason: String,
    /** E1 — "Crédit IA épuisé — remise à zéro le {date}." */
    val exhaustedBannerWebTitle: String,
    val exhaustedBannerWebMessage: String,
    /** E1 — drop zone at 0: the formats without PDF. */
    val dropConstraintsWithoutPdf: String,
    /** E1 — "PDF indisponible jusqu'au {date}" */
    val pdfUnavailableUntil: String,
    /** E1 — "Crédit IA épuisé — ce PDF ne peut pas être analysé" */
    val pdfRefusedTitle: String,
    /** E1 — "« {file} » demande une lecture par l'IA. Votre crédit revient le {date}. …" */
    val pdfRefusedMessage: String,
    /** E1 / M2 — "Colonnes non reconnues — crédit IA épuisé" */
    val spreadsheetExhaustedTitle: String,
    /** E1 — "Les colonnes de « {file} » ne correspondent pas … (retour le {date}). …" */
    val spreadsheetExhaustedWebMessage: String,
    /** E1 — under the expected columns. */
    val expectedColumnsNoCreditHint: String,
    // ── Import stopped for lack of credit (M2, R6, R7) ──
    val importExhaustedTitle: String,
    /** "… La photo n'a pas été analysée et rien n'a été décompté. Remise à zéro le {date}." */
    val importExhaustedPhotoMessage: String,
    /** Same, for a PDF. */
    val importExhaustedFileMessage: String,
    /** "Les colonnes de ce fichier ne correspondent pas … Remise à zéro le {date}." */
    val importSpreadsheetExhaustedMessage: String,
    val stepPhotoSent: String,
    /** "Fichier envoyé : {file}" */
    val stepFileSent: String,
    val stepAnalysisNotStarted: String,
    /** "Fichier lu : {file}" */
    val stepFileRead: String,
    val stepExpectedColumns: String,
    val stepDeliveriesKept: String,
    /** "Réessayer — disponible le {date}" */
    val retryAvailableOn: String,
    val manualEntryAction: String,
    // ── "Mon usage" (M3, E2) ──
    val myUsageTitle: String,
    // Shown only to a Driver attached to an Enterprise: what the Enterprise runs for them is counted on its side.
    val myUsageAttachedDriverNote: String,
    val tabOptimizations: String,
    val tabAiCredit: String,
    val remainingForPeriod: String,
    /** "{percent} %" */
    val percentValue: String,
    /** Web — after the big percentage. */
    val remainingSuffix: String,
    val lowPill: String,
    val exhaustedPill: String,
    val driverNote: String,
    val enterpriseNote: String,
    val exhaustedNote: String,
    val webScopeHint: String,
    /** Profile row value — "{optimizations} · IA {percent} %" */
    val profileValue: String,
    /** Profile row value without a known optimization counter — "IA {percent} %" */
    val profileValueAiOnly: String,
    val columnShare: String,
    val actionScanDeliveryList: String,
    val actionScanPackageLabel: String,
    val actionImportPdf: String,
    val actionImportUnstructured: String,
    val actionSpreadsheetMapping: String,
    val outcomeSuccess: String,
    val outcomeFailed: String,
    val outcomeUnreadable: String,
    /** Mobile row suffix of a failed / unreadable call (R3). */
    val countedSuffix: String,
    /** Mobile row — "{date} à {time}" */
    val rowDateTime: String,
    // ── Admin "Limites d'usage", tab "Tokens IA" (A1) ──
    val limitsEyebrow: String,
    val limitsDescription: String,
    /** "≈ {tokens} tokens pondérés" */
    val weightedEquivalent: String,
    val limitsFootnote: String,
    val conversionEyebrow: String,
    val rateLabel: String,
    val rateHint: String,
    val thresholdLabel: String,
    val thresholdUnit: String,
    val thresholdHint: String,
    val calculationTitle: String,
    val calculationBody: String,
    /** "Limites enregistrées." */
    val settingsSavedTitle: String,
    /** "Entreprise : {enterpriseTrial} essai · {enterprisePaid} payant. Chauffeur : … Taux {rate}. Alerte {threshold} %." */
    val settingsSavedDetail: String,
    val limitTooHigh: String,
    val rateTooHigh: String,
    val thresholdOutOfRange: String,
    // ── Admin "Fiche utilisateur", block "Crédit IA" (A2) ──
    val adminCreditTitle: String,
    /** "≈ {tokens} tokens pondérés restants · consommé {consumed}" */
    val adminRemainingLine: String,
    /** "Consommé {consumed} · dépassement de {over}" */
    val adminOverrunLine: String,
    /** "Crédit bas · sous {threshold} %" */
    val adminLowPill: String,
    val adminLowPillNoThreshold: String,
    val adminExhaustedPill: String,
    /** Applied limit detail after a replacement — "{consumed} consommés + {remaining} restants (remplacement)" */
    val adminAppliedAfterReplacement: String,
    val adminGlobalLimitLabel: String,
    /** "{source} — reprise à la remise à zéro" */
    val adminGlobalLimitResumes: String,
    /** "Repart à {limit} : le dépassement n'est pas reporté" */
    val adminResetNoCarryOver: String,
    val replaceLabel: String,
    val replaceHelp: String,
    /** "Ne modifie que le crédit de {name}. Celui de l'Entreprise et des autres chauffeurs ne change pas." */
    val replaceHelpEnterpriseDriver: String,
    val replaceInvalid: String,
    /** "Crédit restant remplacé : {from} → {to}." */
    val replacedTitle: String,
    /** "Pour la période en cours. Le {date}, retour à la limite globale ({limit})." */
    val replacedDetail: String,
    val adminHistoryTitle: String,
    /** "Période en cours depuis le {start} : {calls} appels · {failed} en échec ou illisible (décomptés) · {tokens} tokens pondérés · {consumed}" */
    val adminPeriodSummary: String,
    val columnInput: String,
    val columnOutput: String,
    val columnWeighted: String,
    val columnCost: String,
    /** Decimal mark of the language — "," or "." — for shares and amounts. */
    val decimalSeparator: String,
    /** M2 — upload or relaunch refused with the 403 (not a job stopped at start): neutral wording. */
    val importExhaustedNeutralMessage: String,
    /** A1 — "1 € =" before the rate field. */
    val rateUnitPrefix: String,
    /** A1 — "$" after the rate field. */
    val rateUnitSuffix: String,
)
